import media from '../media';
import { isDefined } from './defined';
import { spreadByBreakpoints } from './media-spread';

/*
 * Accepts 'fn' and returns polyfill function which
 * both can accept number, string, array (rebass-style: ['16px', '24px', null, '32px']) or object (in a format { xs: num, ..., xl: num })
 * and translates them into a single format { xs: value, ..., xl: value }
 *
 * Example, fillValues = true, props = { xs: 8, md: 16, lg: 24 } -> fn({ xs: 8, sm: 8, md: 16, lg: 24, xl: 24 })
 * Example, fillValues = true, props = [8, null, 16, 24] -> fn({ xs: 8, sm: 8, md: 16, lg: 24, xl: 24 })
 * Example, fillValues = true, props = 8 -> fn({ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 })
 *
 * Example, fillValues = false, props = { xs: 8, md: 16, lg: 24 } -> fn({ xs: 8, md: 16, lg: 24 })
 * Example, fillValues = false, props = [8, null, 16, 24] -> fn({ xs: 8, md: 16, lg: 24 })
 * Example, fillValues = false, props = 8 -> fn({ xs: 8 })
 */
export const byMediaBreakpoints = (fn, fillValues = true) => props => fn(spreadByBreakpoints(props, fillValues));

/*
 * Returns function which applies media rules to the passed 'fn' function argument
 *
 * Example usage:
 *
 * ```
 * export const height = byMedia(value => css<any>`height: ${getPx(value)};`)
 * export const width = byMedia(value => css<any>`width: ${getPx(value)};`)
 *
 * const StyledBox = styled.div<any>`
 *  ${height({ xs: '16px', sm: '24px', xl: '32px' })}
 *  ${width({ xs: '16px', sm: '24px', xl: '32px' })}
 * `
 *
 * const StyledLargeBox = styled.div<any>`
 *  ${height('64px')}
 *  ${width('64px')}
 * `
 *
 * const StyledLargeAnotherBox = styled.div<any>`
 *  ${height(64)}
 *  ${width(64)}
 * `
 * ```
 */
export const byMedia = fn => byMediaBreakpoints(props => Object.keys(props).map(size => isDefined(props[size]) && media.only[size]`${fn(props[size])}`));